import React, { memo, useMemo } from "react";

import {
  Center,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useQuery } from "@apollo/client";

import colors from "app/colors";
import {
  MessageThreadFilesModalQuery,
  MessageThreadFilesModalQueryVariables,
} from "gql/graphql";
import MESSAGE_THREAD_FILES_MODAL_QUERY from "./networking/message-thread-files-modal";
import { isAudio } from "modules/utils/Functions";
import FileGallery from "modules/files/file-gallery";
import { useOverlay } from "modules/providers/overlay-provider";
import { FILE_MODAL_CLOSE_BUTTON_Z_INDEX } from "modules/providers/chakra-provider/chakra-portal-stack";

interface IProps {
  messageThreadId: string;
  fileId?: string;
}

const MessageThreadFileGalleryModal = ({ messageThreadId, fileId }: IProps) => {
  const { closeModal } = useOverlay();
  const { data, error } = useQuery<
    MessageThreadFilesModalQuery,
    MessageThreadFilesModalQueryVariables
  >(MESSAGE_THREAD_FILES_MODAL_QUERY, {
    variables: {
      id: messageThreadId,
    },
    //If the fetchPolicy isn't set to no-cache, the modal will display the wrong initial file
    fetchPolicy: "no-cache",
  });

  const messageThread = useMemo(
    () => data?.messageThread,
    [data?.messageThread],
  );

  const meRemovedUser = messageThread?.meRemovedUser;

  const isUserRemoved = !!meRemovedUser;

  // Filter out audio files; part of the voice notes feature
  // https://teamcake.atlassian.net/browse/SYN-1180
  const files = useMemo(
    () =>
      data?.messageThread?.fileList?.filter(
        (file) => !isAudio(file.contentType),
      ) ?? [],
    [data?.messageThread],
  );

  const initialFile = useMemo(
    () =>
      files.length > 0
        ? files.find((file) => file.id === fileId) || files[0]
        : undefined,
    [files, fileId],
  );

  if (error) {
    return <Text>{error.message}</Text>;
  }

  return (
    <Modal isOpen onClose={closeModal} size="full" isCentered>
      <ModalOverlay />
      <ModalContent h="100vh" bg={colors.white} p={0} m={0}>
        <ModalCloseButton
          color={colors.white}
          zIndex={FILE_MODAL_CLOSE_BUTTON_Z_INDEX}
        />
        <ModalBody
          m={0}
          p={4}
          bg={colors.black}
          className="slider-container"
          minH="100vh"
          maxH="100vh"
        >
          {!messageThread || files.length === 0 || !initialFile ? (
            <Center h="100%">
              <Spinner size="xl" color={colors.white} />
            </Center>
          ) : (
            <FileGallery
              files={files}
              initialFile={initialFile}
              allowDelete={!isUserRemoved}
            />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(MessageThreadFileGalleryModal);
